import { omitEchoedSurveyCommentCompanions } from '@digital-twins/shared-types'

function isStringMap(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return Object.values(value as Record<string, unknown>).every((entry) => typeof entry === 'string')
}

function presentAnswers(entries: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(entries)) {
    if (value === null || value === undefined || value === '') continue
    cleaned[key] = value
  }
  return cleaned
}

function titleForQuestion(
  nameToTitle: Record<string, string>,
  baseName: string,
): string | undefined {
  const title = nameToTitle[baseName]
  if (typeof title !== 'string') return undefined
  const trimmed = title.trim()
  return trimmed || undefined
}

/**
 * Extract clean question→answer pairs from a respondent's raw survey_data blob.
 *
 * The blob is the entire Python runner result dict. The actual Q&A lives at:
 * - `survey_data_by_title` — {humanTitle → answer} (preferred, already parsed)
 * - `survey_data` + `question_name_to_title` — {name → answer} + mapping (fallback)
 *
 * Returns a flat {humanTitle → answerValue} dict ready for display, with all
 * internal runner/observability keys excluded. A SurveyJS comment companion
 * (`name + commentSuffix`) is omitted only when it repeats its own question.
 * Name-keyed data is cleaned before titles are applied. Title-keyed data uses
 * `question_name_to_title` to find that question, and keeps the comment when
 * the link is missing.
 *
 * Cache scope: one-shot per call site, not cached.
 */
export function extractCleanSurveyAnswers(
  rawBlob: Record<string, any>,
): Record<string, unknown> {
  if (rawBlob?.validation_failed === true) {
    return {}
  }

  const nameToTitle: Record<string, string> = isStringMap(rawBlob?.question_name_to_title)
    ? rawBlob.question_name_to_title
    : {}

  const byTitle = rawBlob?.survey_data_by_title
  if (byTitle && typeof byTitle === 'object' && Object.keys(byTitle).length > 0) {
    const cleaned = presentAnswers(byTitle)
    return omitEchoedSurveyCommentCompanions(cleaned, {
      baseKeyFor: (baseName) => titleForQuestion(nameToTitle, baseName),
    })
  }

  const surveyData = rawBlob?.survey_data
  if (surveyData && typeof surveyData === 'object') {
    const withoutEchoes = omitEchoedSurveyCommentCompanions(presentAnswers(surveyData))
    const cleaned: Record<string, unknown> = {}
    for (const [name, value] of Object.entries(withoutEchoes)) {
      cleaned[nameToTitle[name] || name] = value
    }
    return cleaned
  }

  return {}
}
